import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DemoBadge, EmptyState, InfoNote, PriorityBadge } from "@/components/shared";
import { PenTool, TrendingDown } from "lucide-react";
import { toast } from "sonner";

export default function ContentPage() {
  const { activeWebsite } = useApp();
  const navigate = useNavigate();
  const [gaps, setGaps] = useState([]);
  const [decay, setDecay] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!activeWebsite) { setLoading(false); return; }
      setLoading(true);
      try {
        const [{ data: g }, { data: d }] = await Promise.all([
          api.get(`/websites/${activeWebsite.id}/opportunities`, { params: { otype: "content_gap", status: "all" } }),
          api.get(`/websites/${activeWebsite.id}/opportunities`, { params: { otype: "content_decay", status: "all" } }),
        ]);
        setGaps(g.items);
        setDecay(d.items);
      } catch (e) { toast.error(errMsg(e)); }
      setLoading(false);
    })();
  }, [activeWebsite]);

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Content</h1>
        <p className="text-xs text-muted-foreground">Content gaps with real search evidence, and pages losing traction. All new content is quality-gated and requires review before publication.</p>
      </div>

      <Card className="shadow-none">
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><PenTool className="h-4 w-4 text-primary" /> Content gap opportunities</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {gaps.length === 0 ? (
            <EmptyState title="No content gaps detected" message="Gaps appear when queries earn meaningful impressions but no page ranks strongly for them." testId="content-gaps-empty" />
          ) : gaps.map((o) => (
            <div key={o.id} className="rounded-md border p-3" data-testid="content-gap-card">
              <div className="flex flex-wrap items-center gap-2">
                <PriorityBadge priority={o.priority} />
                <span className="font-mono text-xs font-medium">{o.evidence?.query}</span>
                {o.is_demo && <DemoBadge />}
                <span className="ml-auto text-sm font-semibold tabular-nums">{o.score}</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{o.why}</p>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                <Badge variant="outline" className="text-[10px]">impressions: {o.evidence?.impressions?.toLocaleString()}</Badge>
                <Badge variant="outline" className="text-[10px]">best position: {o.evidence?.position}</Badge>
              </div>
            </div>
          ))}
          {gaps.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => navigate("/dashboard/opportunities")} data-testid="content-open-opportunities">Review in Opportunities</Button>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm font-semibold"><TrendingDown className="h-4 w-4 text-amber-600" /> Content decay</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {decay.length === 0 ? (
            <EmptyState title="No decaying content detected" message="Decay compares the last 28 days to the previous 28 days with noise protection - small fluctuations never trigger alarms." testId="content-decay-empty" />
          ) : decay.map((o) => (
            <div key={o.id} className="rounded-md border p-3" data-testid="content-decay-card">
              <div className="flex flex-wrap items-center gap-2">
                <PriorityBadge priority={o.priority} />
                <span className="truncate font-mono text-xs">{o.page}</span>
                {o.is_demo && <DemoBadge />}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{o.why}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge variant="outline" className="text-[10px]">clicks: {o.evidence?.previous_clicks} → {o.evidence?.current_clicks}</Badge>
                <Badge variant="outline" className="border-rose-200 bg-rose-50 text-[10px] text-rose-700">-{o.evidence?.decline_pct}%</Badge>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <InfoNote>
        Content clusters are dynamic per website: services, locations, industries, comparisons, pricing and problem-solving
        content. Programmatic publishing is intentionally constrained - every generated page passes similarity, intent and
        thin-content quality gates (PASS / REVIEW / BLOCK) and requires human approval before it can be indexed.
      </InfoNote>
    </div>
  );
}
